import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import CogitoLogo from './CogitoLogo';
import Button from './Button';
import ThemeToggle from './ThemeToggle';
import useAuth from '../../hooks/useAuth';

const getRelativeTimeLabel = (isoString) => {
  if (!isoString) {
    return 'Now';
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return 'Recently';
  }

  const diffSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSeconds < 60) {
    return 'Just now';
  }
  if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    return `${minutes}m ago`;
  }
  if (diffSeconds < 86400) {
    const hours = Math.floor(diffSeconds / 3600);
    return `${hours}h ago`;
  }
  if (diffSeconds < 604800) {
    const days = Math.floor(diffSeconds / 86400);
    return `${days}d ago`;
  }

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const getAbsoluteDateTime = (isoString) => {
  if (!isoString) {
    return 'Recently';
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return 'Recently';
  }

  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const Header = ({ className = '' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, authPending, user } = useAuth();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const navigationItems = [
    { label: 'Getting Started', path: '/getting-started', icon: 'Sparkles' },
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'My Habits', path: '/habits', icon: 'Target' },
    { label: 'Flashcards', path: '/flashcards', icon: 'Brain' },
    { label: 'Knowledge Base', path: '/knowledge', icon: 'BookOpen' },
    { label: 'Progress', path: '/progress', icon: 'TrendingUp' },
    { label: 'Testing', path: '/testing', icon: 'TestTube' }
  ];

  const supportItems = [
    { label: 'Help Center', path: '/help', icon: 'HelpCircle' }
  ];

  const notifications = useMemo(() => {
    if (!user) {
      return [];
    }

    const entries = [];
    const fullName = user?.full_name?.trim();
    const displayName = fullName || user?.email || 'there';
    const greetingName = fullName?.split?.(' ')?.[0] || user?.email?.split?.('@')?.[0] || 'there';

    if (!user?.email_confirmed_at) {
      entries.push({
        id: 'verify-email',
        title: 'Verify your email',
        description: `Confirm ${user?.email} to unlock reminders.`,
        time: getRelativeTimeLabel(user?.created_at),
        icon: 'Mail',
        read: false
      });
    }

    if (user?.last_sign_in_at) {
      entries.push({
        id: 'last-sign-in',
        title: 'Recent sign-in',
        description: `Last active ${getAbsoluteDateTime(user?.last_sign_in_at)}.`,
        time: getRelativeTimeLabel(user?.last_sign_in_at),
        icon: 'LogIn',
        read: true
      });
    }

    if (user?.updated_at && user?.updated_at !== user?.created_at) {
      entries.push({
        id: 'profile-updated',
        title: 'Profile updated',
        description: `Profile refreshed ${getAbsoluteDateTime(user?.updated_at)}.`,
        time: getRelativeTimeLabel(user?.updated_at),
        icon: 'User',
        read: true
      });
    }

    if (!entries.length) {
      entries.push({
        id: 'no-alerts',
        title: 'All caught up',
        description: `Hi ${greetingName}, you have no new alerts.`,
        time: 'Now',
        icon: 'Bell',
        read: true
      });
    } else {
      entries.unshift({
        id: 'session-summary',
        title: 'Signed in',
        description: `Welcome back, ${displayName}.`,
        time: 'Now',
        icon: 'Sparkles',
        read: true
      });
    }

    return entries;
  }, [user]);

  const unreadCount = useMemo(() => notifications.filter((item) => !item?.read).length, [notifications]);

  const profileMenuItems = useMemo(() => ([
    { label: 'Profile', description: 'View and update your details', path: '/profile', icon: 'User' },
    { label: 'Dashboard', description: 'Return to your insights', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Knowledge', description: 'Browse saved knowledge', path: '/knowledge', icon: 'BookOpen' },
    { label: 'Habits', description: 'Manage your routines', path: '/habits', icon: 'Target' },
    { label: 'Progress', description: 'Track streaks and stats', path: '/progress', icon: 'TrendingUp' },
    { label: 'Settings', description: 'Configure preferences', path: '/settings', icon: 'Settings' },
  ]), []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMenuOpen(false);
    setIsProfileMenuOpen(false);
    setIsNotificationsOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Failed to log out:', error);
    } finally {
      setIsMenuOpen(false);
      setIsProfileMenuOpen(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsNotificationsOpen(false);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen((prev) => !prev);
    setIsNotificationsOpen(false);
  };

  const toggleNotifications = () => {
    setIsNotificationsOpen((prev) => !prev);
    setIsProfileMenuOpen(false);
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 bg-card border-b border-border ${className}`}>
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Logo */}
        <CogitoLogo
          size="sm"
          interactive
          onClick={() => handleNavigation('/dashboard')}
          className="focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-xl"
        />

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1" role="navigation" aria-label="Main navigation">
          {navigationItems?.map((item) => {
            const isActive = isActivePath(item?.path);
            return (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors relative ${
                  isActive 
                    ? 'text-primary bg-primary/10 border border-primary/20' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary rounded-full"></div>
                )}
              </button>
            );
          })}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center space-x-3">
          <ThemeToggle />

          {supportItems?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className="p-2 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-primary/50"
              title={item?.label}
              aria-label={item?.label}
            >
              <Icon name={item?.icon} size={16} />
            </button>
          ))}

          <div className="relative">
            <button
              onClick={toggleNotifications}
              className={`relative p-2 rounded-full border border-border ${
                isNotificationsOpen ? 'text-primary border-primary/40' : 'text-muted-foreground hover:text-foreground hover:border-primary/50'
              }`}
              aria-haspopup="true"
              aria-expanded={isNotificationsOpen}
              aria-label="Notifications"
            >
              <Icon name="Bell" size={16} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {isNotificationsOpen && (
              <div className="absolute right-0 mt-3 w-80 rounded-2xl border border-border bg-card shadow-xl p-3 space-y-3 animate-fade-in">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">Notifications</span>
                  <span className="text-xs text-muted-foreground">{unreadCount} unread</span>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {notifications?.map((item) => (
                    <div
                      key={item?.id}
                      className={`flex items-start space-x-3 rounded-xl border border-border/60 px-3 py-2 ${
                        item?.read ? 'bg-muted/20 text-muted-foreground' : 'bg-primary/5 text-foreground'
                      }`}
                    >
                      <div className="mt-0.5">
                        <Icon name={item?.icon} size={16} className="text-primary" />
                      </div>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium text-foreground">{item?.title}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{item?.description}</p>
                        <span className="text-[11px] uppercase tracking-wide text-muted-foreground/70">{item?.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={toggleProfileMenu}
              className="w-9 h-9 rounded-full border border-border overflow-hidden bg-muted flex items-center justify-center text-xs font-medium text-foreground hover:border-primary/50"
              aria-haspopup="true"
              aria-expanded={isProfileMenuOpen}
              aria-label="Profile menu"
            >
              {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
            </button>

            {isProfileMenuOpen && (
              <div className="absolute right-0 mt-3 w-72 rounded-2xl border border-border bg-card shadow-xl p-3 space-y-3 animate-fade-in">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-sm font-semibold text-foreground bg-muted">
                    {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{user?.full_name || 'Your profile'}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    <button
                      onClick={() => handleNavigation('/profile')}
                      className="mt-2 inline-flex items-center space-x-1 text-xs font-medium text-primary hover:underline"
                    >
                      <Icon name="Sparkles" size={14} />
                      <span>Set status</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  {profileMenuItems.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => handleNavigation(item?.path)}
                      className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40"
                    >
                      <Icon name={item?.icon} size={16} className="text-primary" />
                      <div className="flex-1">
                        <span className="font-medium text-foreground">{item?.label}</span>
                        <p className="text-xs text-muted-foreground">{item?.description}</p>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="border-t border-border/70 pt-3">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => handleNavigation('/settings')}
                      className="text-sm text-muted-foreground hover:text-foreground"
                    >
                      Settings
                    </button>
                    <Button
                      variant="outline"
                      size="xs"
                      iconName="LogOut"
                      onClick={handleLogout}
                      disabled={authPending}
                    >
                      Sign out
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="sm"
          iconName={isMenuOpen ? "X" : "Menu"}
          onClick={toggleMenu}
          className="lg:hidden"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
          aria-label="Toggle navigation menu"
        />
      </div>
      
      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div 
          id="mobile-menu"
          className="lg:hidden bg-card border-t border-border animate-slide-up"
          role="navigation"
          aria-label="Mobile navigation"
        >
          <div className="px-4 py-3 space-y-1">
            {/* Progress Indicator Mobile */}
            {/* Theme Toggle Mobile */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg mb-3">
              <span className="text-sm font-medium text-foreground">Theme</span>
              <ThemeToggle />
            </div>

            {/* Primary Navigation */}
            {navigationItems?.map((item) => {
              const isActive = isActivePath(item?.path);
              return (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`flex items-center space-x-3 w-full p-3 text-left rounded-lg transition-colors ${
                    isActive 
                      ? 'text-primary bg-primary/10 border border-primary/20' :'text-foreground hover:bg-muted'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon name={item?.icon} size={20} />
                  <span className="font-medium">{item?.label}</span>
                  {isActive && (
                    <div className="ml-auto w-2 h-2 bg-primary rounded-full"></div>
                  )}
                </button>
              );
            })}

            {/* Divider */}
            <div className="border-t border-border my-3"></div>

            {/* Secondary Navigation */}
            {supportItems?.map((item) => {
              const isActive = isActivePath(item?.path);
              return (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`flex items-center space-x-3 w-full p-3 text-left rounded-lg transition-colors ${
                    isActive 
                      ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                  {isActive && (
                    <div className="ml-auto w-2 h-2 bg-primary rounded-full"></div>
                  )}
                </button>
              );
            })}

            <div className="border-t border-border my-3"></div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notifications</span>
                <span className="text-[11px] text-muted-foreground/80">{unreadCount} unread</span>
              </div>
              {notifications?.slice(0, 3)?.map((item) => (
                <div
                  key={item?.id}
                  className="flex items-start space-x-3 rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
                >
                  <Icon name={item?.icon} size={18} className="text-primary" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{item?.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item?.description}</p>
                    <span className="text-[11px] uppercase tracking-wide text-muted-foreground/70">{item?.time}</span>
                  </div>
                </div>
              ))}
              {notifications?.length === 0 && (
                <p className="text-xs text-muted-foreground">No notifications yet.</p>
              )}
            </div>

            <div className="border-t border-border my-3"></div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleNavigation('/profile')}
                className="flex-1 flex items-center space-x-2 p-3 bg-muted rounded-lg"
              >
                <div className="w-10 h-10 rounded-full border border-border flex items-center justify-center text-sm font-medium text-foreground">
                  {user?.full_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-semibold text-foreground">{user?.full_name || 'Your profile'}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </button>
              <Button
                variant="outline"
                size="sm"
                iconName="LogOut"
                className="shrink-0"
                onClick={handleLogout}
                disabled={authPending}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;